import type {
  AdminDeliveryProposalDetailPageProps,
  InProgressDeliveryProposalOfferCargoDestination,
} from "@/pages/admin-delivery-proposal-detail/model";
import {
  type CreateDeliveryProposalFormSchemaType,
  type DeliveryProposalDestinationDeadlineFormSchemaType,
  deliveryProposalDestinationDeadlineFormSchema,
} from "@/pages/admin-delivery-proposal-detail/model/validation";
import { DEADLINE_TYPES_LIST } from "@/shared/constants/offer-cargo";
import {
  fromIso8601Date,
  fromIso8601Time,
  isValidDate,
  toIso8601Date,
  toIso8601Time,
} from "@/shared/lib/datetime";
import type { LoadableData } from "@/shared/lib/loadable";
import { customZodResolver } from "@/shared/lib/react-hook-form";
import { BaseActionableDialog } from "@/shared/ui/base-actionable-dialog";
import { BaseDatePicker } from "@/shared/ui/base-date-picker";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseTimeField } from "@/shared/ui/base-time-field";
import { BaseToggle } from "@/shared/ui/base-toggle";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

export const DeadlineFormDialog = (props: {
  open: boolean;
  taskContent: "loading" | "transit" | "unloading";
  draftOfferCargo: LoadableData<
    AdminDeliveryProposalDetailPageProps["draftOfferCargo"]
  >;
  draftOfferCargoDestination: LoadableData<
    AdminDeliveryProposalDetailPageProps["draftOfferCargo"]
  >["destinations"][0];
  currentValues:
    | CreateDeliveryProposalFormSchemaType["loadingDestination"]
    | CreateDeliveryProposalFormSchemaType["unloadingDestination"];
  onSubmit: (
    deadlineDate: string | undefined,
    destination: {
      deadlineType?:
        | NonNullable<
            InProgressDeliveryProposalOfferCargoDestination["deadlineType"]
          >
        | undefined;
      deadlineTime?: string | undefined;
      deadlineTimeEnd?: string | undefined;
    },
  ) => void;
  onClose: () => void;
}) => {
  const { handleSubmit, control, watch, reset, formState, getValues } =
    useForm<DeliveryProposalDestinationDeadlineFormSchemaType>({
      resolver: customZodResolver(
        deliveryProposalDestinationDeadlineFormSchema,
      ),
      mode: "onBlur",
    });

  useEffect(() => {
    reset({ ...props.currentValues });
  }, [props.currentValues, reset]);

  const inputValues = watch();

  return (
    <BaseActionableDialog
      open={props.open}
      title={props.taskContent === "loading" ? "発日時" : "着日時"}
      cancelButtonLabel="キャンセル"
      actionButtonLabel="確定"
      actionButtonDisabled={!formState.isDirty}
      onSubmit={handleSubmit(
        (data) => {
          props.onSubmit(data.deadlineDate, data);
          props.onClose();
        },
        (errors) => {
          console.error(
            "[DeadlineFormDialog] validation error",
            errors,
            getValues(),
          );
        },
      )}
      onClose={props.onClose}
    >
      <Stack spacing={2} pb={4}>
        {(props.taskContent === "loading" ||
          props.taskContent === "unloading") && (
          <BaseFormLabel
            label={props.taskContent === "loading" ? "発日" : "着日"}
          >
            <Controller
              name="deadlineDate"
              control={control}
              render={({ field, fieldState }) => (
                <BaseDatePicker
                  {...field}
                  value={field.value ? fromIso8601Date(field.value) : undefined}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  onChange={(v) => {
                    field.onChange(v ? toIso8601Date(v) : null);
                  }}
                />
              )}
            />
          </BaseFormLabel>
        )}
        <BaseFormLabel label="時間指定">
          <Controller
            name={"deadlineType"}
            control={control}
            render={({ field }) => (
              <BaseToggle
                {...field}
                // 車主からの提案が曖昧になることを避けるため「ちょうど」「時間指定」のみ指定できる
                options={DEADLINE_TYPES_LIST.filter(
                  (type) => type.value === "just" || type.value === "term",
                )}
                size="small"
                onChange={(value) => {
                  field.onChange(value);
                }}
              />
            )}
          />
        </BaseFormLabel>
        <BaseFormLabel label="時間">
          <Box display="flex" gap="10px" alignItems="center">
            <Controller
              name={"deadlineTime"}
              control={control}
              render={({ field, fieldState }) => {
                return (
                  <BaseTimeField
                    {...field}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    value={
                      field.value ? fromIso8601Time(field.value) : undefined
                    }
                    onChange={(v) => {
                      field.onChange(
                        isValidDate(v) ? toIso8601Time(v) : undefined,
                      );
                    }}
                  />
                );
              }}
            />
            {inputValues.deadlineType === "term" && (
              <>
                <Typography justifyItems="Center">–</Typography>
                <Controller
                  name={"deadlineTimeEnd"}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <BaseTimeField
                        {...field}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        value={
                          field.value ? fromIso8601Time(field.value) : undefined
                        }
                        onChange={(v) => {
                          field.onChange(
                            isValidDate(v) ? toIso8601Time(v) : undefined,
                          );
                        }}
                      />
                    );
                  }}
                />
              </>
            )}
          </Box>
        </BaseFormLabel>
      </Stack>
    </BaseActionableDialog>
  );
};
